import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { compact } from 'lodash/fp';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { EnableFor } from '../../../containers/EnableForRole';
import { AnnotationMoreOptions } from '../../annotation/components/AnnotationMoreOptions';
import { AnnotationTopBarItem } from '../../annotation/components/AnnotationTopBarItem';
import { AnnotationViewOptions } from '../../annotation/components/AnnotationViewOptions';
import { AnnotationImageNameHeaderContainer } from '../../annotation/containers/AnnotationImageNameHeader.container';
import { NoAnnotationTypesWarningContainer } from '../../annotation/containers/NoAnnotationTypesWarningContainer';
import { QuestionsContainer } from '../../annotation/containers/Questions.container';
import { ReturnToToolsContainer } from '../../annotation/containers/ReturnToTools.container';
import { SegmentationsSaveContainer } from '../../annotation/containers/SegmentationsSave.container';
import { UndoRedoAnnotationsShortcutsContainer } from '../../annotation/containers/UndoRedoAnnotationsShortcuts.container';
import { ViewAttributesContainer } from '../../annotation/containers/ViewAttributesContainer';
import { AnnotationTypesContainer } from '../../annotation/submodules/annotationTypes/containers/AnnotationTypes.container';
import { LeftMenuContainer } from '../../annotation/submodules/segmentations/containers/LeftMenu.container';
import { ImageSegmentationsContextProvider } from '../../annotation/submodules/segmentations/segmentationContextMenu/imageSegmentations.context';
import { UnlockWorkspaceModal } from '../../workspaces/components/UnlockWorkspaceModal';
import { CurrentWorkspaceContainer } from '../../workspaces/containers/CurrentWorkspace.container';
import { BackToProjectDetailsButton } from '../components/backToProjectDetailsButton';
import { FreeAccessImageNavigation } from '../components/FreeAccessImageNavigation';
import { useFreeAccessPermissions } from '../freeAccess.permissions';
import { IFreeAccessService } from '../freeAccess.service';
import { AnnotateStage, FreeAccessMode, IFreeAccessStore } from '../freeAccess.store';
import { FreeAccessActionsContainer } from './freeAccessActions.container';
import { FreeAccessAnnotateControlsContainer } from './freeAccessAnnotateControls.container';
import { FreeAccessImageSetupContainer } from './FreeAccessImageSetup.container';
import { FreeAccessNavigationContainer } from './freeAccessNavigation.container';
import { FreeAccessReviewControlsContainer } from './freeAccessReviewControls.container';
import { FreeAccessReviewControlsCorrectContainer } from './freeAccessReviewCorrectControls.container';

export const FreeAccessContainer = observer((): ReactElement => {
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const freeAccessService = useInjection(IFreeAccessService.$);
  const permissions = useFreeAccessPermissions();

  const { projectId, workspaceId, imageId, filterId } = useParams<{ workspaceId: string; projectId: string; imageId: string; filterId: string }>();

  useEffect(() => {
    freeAccessStore.setView(FreeAccessMode.VIEW, AnnotateStage.NOTANNOTATED);
    freeAccessStore.imageId = '';

    return (): void => {
      void unlockImages();
    };
  }, []);

  const unlockImages = async (): Promise<void> => {
    if (freeAccessStore.freeAccessMode !== FreeAccessMode.VIEW) {
      await freeAccessService.unlockImagesAsync();
    }
    freeAccessService.imagesQueueService.clear();
  };

  const getAvailableShortcuts = (): { label: TranslationKey; keys: string[] }[] => {
    const stage = freeAccessStore.annotateStage;

    return compact([
      stage !== AnnotateStage.NOTANNOTATED && { label: 'edit', keys: ['e'] },
      { label: 'annotate', keys: ['a'] },
      [AnnotateStage.ANNOTATED, AnnotateStage.REVIEWED].includes(stage) && { label: 'review', keys: ['r'] },
    ]);
  };

  const mode = freeAccessStore.freeAccessMode;
  const isReadOnly = mode === FreeAccessMode.VIEW || mode === FreeAccessMode.REVIEW;

  return (
    <div className='annotation-view free-access'>
      <CurrentWorkspaceContainer>{(unlockModalProps): ReactElement => <UnlockWorkspaceModal {...unlockModalProps} />}</CurrentWorkspaceContainer>
      <ImageSegmentationsContextProvider imageId={imageId}>
        <FreeAccessNavigationContainer
          workspaceId={workspaceId}
          projectId={projectId}
          filterId={filterId}
          callback={unlockImages}
        >
          {(freeNav, backNav): ReactElement => (
            <>
              <div className='annotation-view-top-bar'>
                <AnnotationTopBarItem>
                  <BackToProjectDetailsButton {...backNav} />
                </AnnotationTopBarItem>
                {!isReadOnly && (
                  <>
                    <AnnotationTopBarItem>
                      <UndoRedoAnnotationsShortcutsContainer undo={true} />
                    </AnnotationTopBarItem>
                    <AnnotationTopBarItem>
                      <UndoRedoAnnotationsShortcutsContainer undo={false} />
                    </AnnotationTopBarItem>
                  </>
                )}
                <AnnotationTopBarItem>
                  <AnnotationViewOptions disableCursorConfig={[FreeAccessMode.VIEW, FreeAccessMode.REVIEW].includes(mode)} />
                </AnnotationTopBarItem>
                <AnnotationTopBarItem>
                  <ViewAttributesContainer />
                </AnnotationTopBarItem>
                <EnableFor
                  hideWhenNoAccess
                  hasPermission={permissions.canSeeImageName()}
                >
                  <AnnotationTopBarItem variation='image-name'>
                    <AnnotationImageNameHeaderContainer />
                  </AnnotationTopBarItem>
                </EnableFor>
                <AnnotationTopBarItem>
                  <FreeAccessImageNavigation {...freeNav} />
                </AnnotationTopBarItem>
                <AnnotationTopBarItem>
                  <AnnotationMoreOptions
                    isFreeAccess={true}
                    create={mode === FreeAccessMode.ANNOTATION || mode === FreeAccessMode.ANNOTATIONRESET || mode === FreeAccessMode.CORRECT}
                    review={mode === FreeAccessMode.REVIEW}
                    correct={mode === FreeAccessMode.REVIEWCORRECT}
                    edit={mode === FreeAccessMode.ANNOTATIONRESET}
                    globalShortcuts={mode === FreeAccessMode.VIEW ? getAvailableShortcuts() : []}
                  />
                </AnnotationTopBarItem>
              </div>
              <div className='annotation-view-inner'>
                <LeftMenuContainer
                  currentProjectId={projectId}
                  isReadOnly={isReadOnly}
                  showObjectsList={true}
                  isReview={false}
                />
                <FreeAccessImageSetupContainer
                  isReadOnly={isReadOnly}
                  imageId={imageId}
                  projectId={projectId}
                  workspaceId={workspaceId}
                  filterId={filterId}
                />
                <div className='right-column'>
                  <div className='set-navigation-container'>
                    <FreeAccessActionsContainer />
                  </div>
                  <div className='scrollable-container'>
                    <ReturnToToolsContainer readonly={isReadOnly} />
                    <div className='scrollable-container-content'>
                      <NoAnnotationTypesWarningContainer />
                      <AnnotationTypesContainer readonly={isReadOnly} />
                      <QuestionsContainer readonly={isReadOnly} />
                      {!isReadOnly && <SegmentationsSaveContainer />}
                    </div>
                  </div>
                  {(mode === FreeAccessMode.ANNOTATION || mode === FreeAccessMode.ANNOTATIONRESET || mode === FreeAccessMode.CORRECT) && (
                    <FreeAccessAnnotateControlsContainer />
                  )}
                  {mode === FreeAccessMode.REVIEW && <FreeAccessReviewControlsContainer />}
                  {mode === FreeAccessMode.REVIEWCORRECT && <FreeAccessReviewControlsCorrectContainer />}
                </div>
              </div>
            </>
          )}
        </FreeAccessNavigationContainer>
      </ImageSegmentationsContextProvider>
    </div>
  );
});
